import React from 'react';
import BaseModal from '../../../utils/BaseModal';
import { BuyOrder } from './UserBuyOrder';
import { moneyFromMinor } from '../../../utils/UnitConversion.utils';
import { useCurrency } from '../../../../hooks/useCurrency';
import { SteamAccount } from '../../../../store/slices/authSlice';

type Props = {
  open: boolean;
  order: BuyOrder | null;
  onClose: () => void;
  account?: SteamAccount;
};

const BuyOrderInfoModal: React.FC<Props> = ({ open, order, onClose, account }) => {
  const { convertFromUSD, currencySymbol, formatAmountWithoutCurrency } = useCurrency();
  if (!order) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'COMPLETED': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'PAUSED': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'CANCELLED': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-neutral-500/10 text-neutral-500 border-neutral-500/20';
    }
  };

  const row = (label: string, value: React.ReactNode, className?: string) => (
      <div className="flex justify-between items-center py-3 border-b border-neutral-100 dark:border-neutral-800/50 last:border-0">
        <span className="text-xs uppercase tracking-wider font-bold text-neutral-500">{label}</span>
        <div className={`text-sm font-semibold dark:text-neutral-200 ${className}`}>{value}</div>
      </div>
  );

  return (
      <BaseModal
          open={open}
          title="Buy Order Details"
          onAbort={onClose}
      >
        <div className="flex flex-col gap-6">
          {/* Header Card mit Bild und Badge */}
          <div className="relative group overflow-hidden bg-gradient-to-b from-neutral-50 to-white dark:from-neutral-800/50 dark:to-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl p-8 flex flex-col items-center shadow-inner">
            <div className={`absolute top-4 right-4 px-2.5 py-1 rounded-md text-[10px] font-black border tracking-tighter ${getStatusColor(order.status)}`}>
              {order.status}
            </div>

            <img
                src={order.imageLink}
                referrerPolicy="no-referrer"
                className="w-40 h-40 object-contain drop-shadow-2xl transform group-hover:scale-105 transition-transform duration-300"
                alt={order.itemName}
            />

            <h3 className="mt-4 text-center font-bold text-lg leading-tight dark:text-white max-w-[80%]">
              {order.itemName}
            </h3>
          </div>

          {/* Details List */}
          <div className="bg-neutral-50/50 dark:bg-black/20 rounded-xl px-4 py-2 border border-neutral-100 dark:border-neutral-800">
            {row('Quantity', (
                <div className="flex items-center gap-2">
                  <span className="text-emerald-500">{order.completedQuantity}</span>
                  <span className="text-neutral-500 font-light">/</span>
                  <span>{order.quantity}</span>
                </div>
            ))}
            {row('Unit Price', (
                <span className="text-emerald-500">
                  {currencySymbol}{formatAmountWithoutCurrency(convertFromUSD(moneyFromMinor(order.priceMinor)))}
                </span>
            ))}
            {row('Total Value', (
                <span className="text-white bg-neutral-800 dark:bg-neutral-700 px-2 py-0.5 rounded">
                  {currencySymbol}{formatAmountWithoutCurrency(convertFromUSD(moneyFromMinor(order.priceMinor * order.quantity)))}
                </span>
            ))}
            {row('Steam Account', (
                <div className="flex items-center gap-2">
                  {account?.avatarUrl && <img src={account.avatarUrl} className="w-5 h-5 rounded-full border border-neutral-600" alt="" />}
                  <span className="truncate max-w-[120px]">{account?.profileName || 'Unknown'}</span>
                </div>
            ))}
            {row('Created At', new Date(order.creationDate).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }), 'text-neutral-400 font-normal')}
            {row('Order ID', <span className="font-mono text-neutral-500">#{order.id}</span>)}
          </div>
        </div>
      </BaseModal>
  );
};

export default BuyOrderInfoModal;